// src/observer/videoAuto.ts

import { ensureBufferingIndicator } from "../utils/bufferingIndicator";

// Back-compat: older snippets used `HTMLPreload`; alias it to the correct DOM type.
type HTMLPreload = HTMLVideoElement["preload"];

type PlaybackManagedVideo = HTMLVideoElement & {
  __scPlaybackCleanup?: () => void;
};

function cleanupManagedPlayback(video: HTMLVideoElement): void {
  const managed = video as PlaybackManagedVideo;
  try {
    managed.__scPlaybackCleanup?.();
  } catch {}
  delete managed.__scPlaybackCleanup;
}

function shouldKeepTrying(section: HTMLElement, video: HTMLVideoElement): boolean {
  if (!document.body.contains(video)) return false;
  if (!section.classList.contains("visible")) return false;
  if (video.dataset.autoplay === "false") return false;
  return true;
}

function scheduleManagedPlayback(section: HTMLElement, video: HTMLVideoElement): void {
  cleanupManagedPlayback(video);

  const disposers: Array<() => void> = [];
  const timers: number[] = [];
  const managed = video as PlaybackManagedVideo;

  const cleanup = () => {
    while (timers.length) {
      window.clearTimeout(timers.pop()!);
    }
    while (disposers.length) {
      try { disposers.pop()!(); } catch {}
    }
    delete managed.__scPlaybackCleanup;
  };
  managed.__scPlaybackCleanup = cleanup;

  const attemptPlay = () => {
    if (!shouldKeepTrying(section, video)) {
      cleanup();
      return;
    }

    try {
      const playResult = video.play();
      if (playResult && typeof (playResult as any).then === "function") {
        (playResult as Promise<void>)
          .then(() => {
            cleanup();
          })
          .catch(() => {});
      } else if (!video.paused) {
        cleanup();
      }
    } catch {}
  };

  const requestLoad = () => {
    try {
      if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        video.load();
      }
    } catch {}
  };

  const onReady = () => {
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      attemptPlay();
    }
  };

  const onSlowNetwork = () => {
    if (!shouldKeepTrying(section, video) || !video.paused) return;
    requestLoad();
    const timer = window.setTimeout(() => {
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        attemptPlay();
      }
    }, 150);
    timers.push(timer);
  };

  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    attemptPlay();
  } else {
    requestLoad();
  }

  [
    "loadeddata",
    "canplay",
    "canplaythrough",
  ].forEach((eventName) => {
    const handler = () => onReady();
    video.addEventListener(eventName, handler, { once: true });
    disposers.push(() => video.removeEventListener(eventName, handler));
  });

  ["waiting", "stalled", "suspend"].forEach((eventName) => {
    const handler = () => onSlowNetwork();
    video.addEventListener(eventName, handler);
    disposers.push(() => video.removeEventListener(eventName, handler));
  });

  [250, 900, 1800, 3200].forEach((ms) => {
    const timer = window.setTimeout(() => {
      if (!shouldKeepTrying(section, video)) {
        cleanup();
        return;
      }
      if (!video.paused) {
        cleanup();
        return;
      }
      if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        requestLoad();
      }
      attemptPlay();
    }, ms);
    timers.push(timer);
  });
}

/**
 * Prepare a single <video> for autoplay & policy compliance, then (optionally) start it.
 * Respects:
 *  - data-autoplay="false"          => don't auto-start
 *  - data-reset-on-leave="true"     => when pausing (on section leave), rewind to 0
 *  - data-preload="auto|metadata|none" (default metadata unless explicitly auto)
 *  - loop (attribute)               => for non-reels, default loop=true unless author set loop:false
 *
 * Rules we enforce here:
 *  - Background/inline video: loop by default; if author set loop:false => play once
 *  - Reels: NEVER loop; HUD + observer control audio & playback (no blanket mute here)
 */
export function handleSectionVideo(opts: {
  section: HTMLElement;
  video?: HTMLVideoElement;
}): void {
  const section = opts.section;
  const video = opts.video ?? section.querySelector<HTMLVideoElement>("video");

  if (!section) {
    console.warn("[OBSERVER] handleSectionVideo: missing section");
    return;
  }
  if (!video) {
    console.warn("[OBSERVER] handleSectionVideo: section has no video element");
    return;
  }

  // Wire up the visible buffering indicator. videoAuto already retries on
  // stall events via scheduleManagedPlayback; this adds the UI layer that
  // tells the user a stall is happening. Idempotent — duplicate calls
  // (e.g. via watchForLateVideos) are no-ops.
  ensureBufferingIndicator(video);

  // Detect reels (stamped by HUD / authoring)
  const isReel =
    video.dataset.reel === "true" ||
    video.dataset.role === "reel" ||
    section.dataset.hudType === "reel" ||
    !!section.querySelector('video[data-reel="true"], .video-reel');

  // Autoplay policy: default to "observe" (let the observer play/pause on enter/leave)
  // data-autoplay="immediate" → attempt play as soon as it can
  // data-autoplay="false|off|none" → never auto-attempt
  const autoplayMode = (video.dataset.autoplay || "observe").toLowerCase();
  const autoplayImmediate = autoplayMode === "immediate";
  const autoplayDisabled  = autoplayMode === "false" || autoplayMode === "off" || autoplayMode === "none";

  // Baseline for autoplay policies
  video.autoplay = true;
  video.controls = false;
  video.setAttribute("playsinline", "");
  video.setAttribute("webkit-playsinline", ""); // iOS inline (older WebKit)
  video.playsInline = true;

  // Audio posture:
  // - Background/inline: keep muted for autoplay stability
  // - Reels: leave mute decision to HUD/global first-run logic
  if (!isReel) {
    if (!video.muted) {
      video.muted = true;
      video.setAttribute("muted", "");
    }
  }

  // Preload strategy: prefer metadata unless author opted into auto
  // (prevents many off-screen videos from pulling full data at once)
  const rawPref = (video.dataset.preload || "").toLowerCase();
  const allowed = ["auto", "metadata", "none"] as const;
  const pref: HTMLVideoElement["preload"] =
    (allowed as readonly string[]).includes(rawPref)
      ? (rawPref as HTMLVideoElement["preload"])
      : "metadata";

  if (!video.hasAttribute("preload")) {
    video.preload = pref;
  }

  // Looping policy
  // - Reels: default to NO loop (play once → replay icon shown at end).
  //   Opt-in to looping via markdown `loop:true` → data-loop="true" on the
  //   video. When looped, the replay icon never appears because `ended`
  //   never fires.
  // - Non-reels: loop by default unless author set loop:false (markdown → data-loop="false")
  const authoredNoLoop =
    (video.dataset.loop || "").toLowerCase() === "false" ||
    video.getAttribute("loop") === "false"; // tolerate bad HTML from upstream
  const authoredLoop =
    (video.dataset.loop || "").toLowerCase() === "true" ||
    video.hasAttribute("loop");

  if (isReel) {
    if (authoredLoop) {
      // Author explicitly opted into reel looping
      video.loop = true;
      video.setAttribute("loop", "");
      video.dataset.loop = "true";
    } else {
      video.loop = false;
      video.removeAttribute("loop");
      video.dataset.loop = "false";
    }
  } else {
    if (authoredNoLoop) {
      video.loop = false;
      video.removeAttribute("loop");
      video.dataset.loop = "false";
    } else if (!video.hasAttribute("loop")) {
      // default to loop for non-reel if not explicitly turned off
      video.loop = true;
      // optional: video.setAttribute("loop", "");
      video.dataset.loop = "true";
    }
  }

  // Nice-to-have posture on controls
  try {
    (video as any).disableRemotePlayback = true;
  } catch {}
  try {
    (video as any).controlsList?.add?.("nodownload", "nofullscreen", "noremoteplayback");
  } catch {}

  // Safari black-first-frame nudge
  const onCanPlayNudge = () => {
    try {
      if (video.currentTime === 0) video.currentTime = 0.001;
    } catch {}
  };
  video.addEventListener("canplay", onCanPlayNudge, { once: true });

  if (autoplayDisabled || !autoplayImmediate) {
    // We still set correct attributes and the Safari nudge above,
    // but we do NOT attempt to play here. The observer handles playback on enter.
    return;
  }

  const tryPlay = () => {
    try {
      if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        try { video.load(); } catch {}
        scheduleManagedPlayback(section, video);
        return;
      }

      const p = video.play();
      if (p && typeof (p as any).then === "function") {
        (p as Promise<void>)
          .then(() => {
            video.dataset.scPlayed = "1";
            markSomeVideoStarted();
          })
          .catch((err) => {
            // Only prompt once per video
            if (video.dataset.scPrompted === "1") return;
            video.dataset.scPrompted = "1";

            console.warn("[OBSERVER] Video autoplay blocked:", err);
            if (autoplayImmediate && isReel) {
              console.warn("[REEL] Autoplay blocked — falling back to muted autoplay");
              try {
                video.muted = true;
                video.setAttribute("muted", "");
                scheduleManagedPlayback(section, video);
              } catch {}
            }
          });
      }
    } catch (err) {
      if (video.dataset.scPrompted !== "1") {
        video.dataset.scPrompted = "1";
        console.warn("[OBSERVER] video.play() threw:", err);
        if (autoplayImmediate && isReel) {
          console.warn("[REEL] Autoplay blocked — falling back to muted autoplay");
          try {
            video.muted = true;
            video.setAttribute("muted", "");
            scheduleManagedPlayback(section, video);
          } catch {}
        }
      }
    }
  };

  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    tryPlay();
  } else {
    video.addEventListener("canplay", tryPlay, { once: true });
    try {
      video.load();
    } catch {}
  }
}

/**
 * Wire a section to automatically initialize any <video> children it has now or later.
 * Idempotent per-section.
 */
export function watchForLateVideos(section: HTMLElement) {
  if ((section as any).__scVideoWatcher) return; // idempotent
  const seen = new WeakSet<HTMLVideoElement>();

  const tryWire = (v: HTMLVideoElement) => {
    if (seen.has(v)) return;
    seen.add(v);
    handleSectionVideo({ section, video: v });
  };

  // Wire any existing videos right now
  section.querySelectorAll<HTMLVideoElement>("video").forEach(tryWire);

  const mo = new MutationObserver((muts) => {
    for (const m of muts) {
      m.addedNodes.forEach((n) => {
        if (n instanceof HTMLVideoElement) tryWire(n);
        else if (n instanceof HTMLElement) {
          n.querySelectorAll?.("video").forEach((v) => tryWire(v as HTMLVideoElement));
        }
      });
    }
  });
  mo.observe(section, { subtree: true, childList: true });
  (section as any).__scVideoWatcher = mo;

  // When section is hidden via old CSS class flows, stop watching.
  section.addEventListener(
    "transitionend",
    () => {
      if (!section.classList.contains("visible")) {
        try {
          mo.disconnect();
        } catch {}
        delete (section as any).__scVideoWatcher;
      }
    },
    { passive: true }
  );
}

/** Play all videos inside a section (used on section enter). */
export function playSectionVideos(section: HTMLElement) {
  section.querySelectorAll<HTMLVideoElement>("video").forEach((v) => {
    if (v.dataset.autoplay === "false") return; // respect author intent

    // Ensure autoplay posture still holds (in case DOM was re-rendered)
    const isReel =
      v.dataset.reel === "true" ||
      v.dataset.role === "reel" ||
      section.dataset.hudType === "reel";

    if (!isReel) {
      v.muted = true; // keep backgrounds muted by default for autoplay
      v.setAttribute("muted", "");
    }

    // Mark that this video is explicitly allowed to run while its section is active.
    try {
      v.dataset.userAllowed = "1";
    } catch {}

    v.playsInline = true;
    v.setAttribute("playsinline", "");
    v.setAttribute("webkit-playsinline", "");

    const attemptPlay = () => {
      scheduleManagedPlayback(section, v);
    };

    attemptPlay();
  });
}

/** Pause (and optionally rewind) all videos inside a section (used on section leave). */
export function pauseSectionVideos(section: HTMLElement) {
  section.querySelectorAll<HTMLVideoElement>("video").forEach((v) => {
    cleanupManagedPlayback(v);
    try {
      v.pause();
    } catch {}
    try {
      delete v.dataset.userAllowed;
    } catch {}
    if (v.dataset.resetOnLeave === "true") {
      try {
        v.currentTime = 0;
      } catch {}
    }
  });
}

/**
 * Optional convenience: listen for custom lifecycle events on the section.
 * If your ScrollTrigger/observer dispatches:
 *   section.dispatchEvent(new Event('sc:enter'));
 *   section.dispatchEvent(new Event('sc:leave'));
 * …this will play/pause videos automatically.
 */
export function wireSectionVideoLifecycle(section: HTMLElement) {
  if ((section as any).__scVideoLifecycle) return; // idempotent
  const onEnter = () => playSectionVideos(section);
  const onLeave = () => pauseSectionVideos(section);
  section.addEventListener("sc:enter", onEnter as EventListener);
  section.addEventListener("sc:leave", onLeave as EventListener);
  (section as any).__scVideoLifecycle = { onEnter, onLeave };
}

// global one-shot resume for blocked autoplay
function setupGlobalAutoplayResume() {
  if ((window as any).__scAutoResumeHooked) return;
  (window as any).__scAutoResumeHooked = true;

  const resumeAll = () => {
    document.querySelectorAll<HTMLVideoElement>("section video").forEach((v) => {
      // Don't wake finished non-loop backgrounds or finished play-once reels
      const isBg = v.dataset.role === "background" || v.dataset.bg === "true";
      const authoredNoLoop = v.dataset.loop === "false" || !v.loop;
      const bgFinished = v.ended || v.dataset.bgFinished === "1";

      const isReel = v.dataset.reel === "true" || v.dataset.role === "reel";
      const reelOnce = v.dataset.reelPlayOnce === "true";
      const reelFinished = v.ended || v.dataset.reelPlayed === "1";

      if ((isBg && authoredNoLoop && bgFinished) || (isReel && reelOnce && reelFinished)) return;

      try {
        v.play().catch(() => {});
      } catch {}
    });
    teardown();
  };
  const teardown = () => {
    window.removeEventListener("pointerdown", resumeAll, true);
    window.removeEventListener("click", resumeAll, true);
    window.removeEventListener("touchend", resumeAll, true);
    window.removeEventListener("keydown", resumeAll, true);
    (window as any).__scAutoResumeHooked = false;
  };

  window.addEventListener("pointerdown", resumeAll, { capture: true, once: true });
  window.addEventListener("click", resumeAll, { capture: true, once: true });
  window.addEventListener("touchend", resumeAll, { capture: true, once: true });
  window.addEventListener("keydown", resumeAll, { capture: true, once: true });
}

function markSomeVideoStarted() {
  // reserved for future state; keeps hook idempotent
}
